import clsx from 'clsx'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { ItemOrderCardPriceProps } from './type'

export const ItemOrderCardPrice = ({
  item,
  cartSalesTypeQuantities,
  direction,
  showCount = true,
  showPurchasePrice = false,
}: ItemOrderCardPriceProps) => {
  const { t } = useTranslation()
  const count = useMemo(
    () =>
      cartSalesTypeQuantities
        ? cartSalesTypeQuantities.reduce(
            (sum, current) => (sum += current.quantity),
            0
          )
        : 0,
    [cartSalesTypeQuantities]
  )

  const summarySalonPrice = useMemo(() => {
    if (!item.salonPrice || !cartSalesTypeQuantities) {
      return 0
    }

    return item.salonPrice * count
  }, [item, cartSalesTypeQuantities, count])

  const purchasePriceSummary = useMemo(() => {
    if (!item.salonPrice) {
      return 0
    }
    if (!cartSalesTypeQuantities || cartSalesTypeQuantities.length === 0) {
      return 0
    }

    return cartSalesTypeQuantities.reduce(
      (prev, next) => prev + next.purchasePrice * next.quantity,
      0
    )
  }, [cartSalesTypeQuantities, item.salonPrice])

  // Ntoe: 購入価格の平均値を表示する
  const averagePurchasePrice = useMemo(() => {
    if (!cartSalesTypeQuantities || cartSalesTypeQuantities.length === 0) {
      return 0
    }
    const itemCount = cartSalesTypeQuantities.reduce((prev, next) => {
      return prev + next.quantity
    }, 0)

    if (itemCount === 0) return 0

    const average = purchasePriceSummary / itemCount

    return average % 1 === 0 ? average : average.toFixed(2)
  }, [cartSalesTypeQuantities, purchasePriceSummary])

  switch (direction) {
    case 'horizontal': {
      return (
        <div
          className={clsx('tw-flex tw-flex-wrap tw-items-center', 'tw-gap-x-1')}
        >
          <div
            className={clsx(
              'tw-flex tw-flex-wrap tw-items-center',
              'tw-gap-x-4'
            )}
          >
            {showCount && (
              <p className={clsx('tw-text-xs')}>{`${t(
                'item.order.quantity'
              )}：${count}${t('item.order.units')}`}</p>
            )}

            <p className={clsx('tw-text-xs tw-text-gray-600')}>
              {`${t('item.order.salonPrice')}：${(
                item.salonPrice || 0
              ).toLocaleString()}${t('item.order.currency')}`}
            </p>
            {cartSalesTypeQuantities && (
              <p className={clsx('tw-text-xs tw-text-gray-600')}>
                {`${t(
                  'item.order.total'
                )}：${summarySalonPrice.toLocaleString()}${t(
                  'item.order.currency'
                )}`}
              </p>
            )}
            {showPurchasePrice && purchasePriceSummary && (
              <p className={clsx('tw-text-xs', 'tw-bg-gray-100', 'tw-p-2')}>
                {`${t(
                  'item.order.purchasePriceTotal'
                )}：${purchasePriceSummary.toLocaleString()}${t(
                  'item.order.currency'
                )}`}
              </p>
            )}
          </div>
        </div>
      )
    }
    case 'vertical': {
      return (
        <table
          className={clsx(
            'tw-text-sm',
            'tw-table-fix',
            'tw-w-full',
            'tw-border-separate tw-border-spacing-y-1'
          )}
        >
          {showCount && (
            <tr>
              <td>{t('item.order.quantity')}</td>
              <td className={clsx('tw-min-w-[120px] tw-text-right')}>
                {count}
                {t('item.order.units')}
              </td>
            </tr>
          )}
          <tr className={clsx('tw-text-gray-600')}>
            <td>{t('item.order.salonPrice')}</td>
            <td className={clsx('tw-min-w-[120px] tw-text-right')}>{`${(
              item.salonPrice || 0
            ).toLocaleString()}${t('item.order.currency')}`}</td>
          </tr>
          {cartSalesTypeQuantities && (
            <tr className={clsx('tw-text-gray-600')}>
              <td>{t('item.order.total')}</td>
              <td
                className={clsx('tw-min-w-[120px] tw-text-right')}
              >{`${summarySalonPrice.toLocaleString()}${t(
                'item.order.currency'
              )}`}</td>
            </tr>
          )}
          {showPurchasePrice && purchasePriceSummary && (
            <tr className={clsx('tw-bg-gray-100')}>
              <td className={clsx('tw-py-2 tw-pl-2', 'tw-space-y-1')}>
                <span className={clsx('tw-block')}>
                  {t('item.order.averagePurchasePrice')}
                </span>
                <span className={clsx('tw-block')}>
                  {t('item.order.purchasePriceTotal')}
                </span>
              </td>
              <td
                className={clsx(
                  'tw-min-w-[120px] tw-text-right',
                  'tw-py-2 tw-pr-2',
                  'tw-space-y-1'
                )}
              >
                <span
                  className={clsx('tw-block')}
                >{`${averagePurchasePrice.toLocaleString()}${t(
                  'item.order.currency'
                )}`}</span>
                <span
                  className={clsx('tw-block')}
                >{`${purchasePriceSummary.toLocaleString()}${t(
                  'item.order.currency'
                )}`}</span>
              </td>
            </tr>
          )}
        </table>
      )
    }
    default: {
      return <></>
    }
  }
}
